import _ from 'lodash';
import template from './employerJobsView.html';
import { CANDIDATE_FEEDBACK_STATUSES } from '../../recruiter/constants/feedback';
import BaseComponent from '../../base/component';
import { Async, Inject, withAngularComponent } from '../../base/decorators';
import './activity-report-csv-export';
import './style.scss';
import {
    COUNTER_ALL,
    COUNTER_GOOD_FIT,
    COUNTER_NON_RATED,
    COUNTER_NOT_FIT,
} from '../../common/tft-feedback-filter/component';
import { POSITION_EVENT_FLOW_TYPES, POSITION_EVENTS } from '../../../constants/positionEvents';
import {
    datePosted, filterEmployerJobsByEmployerRepId,
    filterEmployerJobsByOpenCloseState,
    filterEmployerJobsByStatus,
    filterEmployerJobsByTitle,
    getFirstActiveJobDate,
} from '../../core/filters/commonFilters';
import { positionStatuses } from '../../../constants/position';
import { AuthService } from '../../core/authentication/authenticationService';

const OPEN_POSITION_HANDLER = 'OPEN_POSITION';

const WORKFLOW_COUNTERS_CHUNK_SIZE = 12;

const filterDatePostedOptions = [
    {
        value: 'ALL',
        label: 'ALL',
    },
    {
        value: 'LAST_WEEK',
        label: 'WEEK',
    },
    {
        value: '2_WEEKS_AGO',
        label: 'TWO_WEEKS',
    },
    {
        value: '30_DAYS',
        label: '30_DAYS',
    },
    {
        value: '60_DAYS',
        label: '60_DAYS',
    },
    {
        value: '120_DAYS',
        label: '120_DAYS',
    },
];

const filterPositionStatusOptions = [
    {
        value: 'ALL',
        label: 'ALL',
    },
    {
        value: 'COMPLETE_JOB_DEFINITION',
        label: 'COMPLETE_JOB_BUTTON',
    },
    {
        value: 'START_RATING',
        label: 'START_RATING',
    },
    {
        value: 'COMPLETE_RATING',
        label: 'COMPLETE_RATING',
    },
];

const filterOpenClosedStatusOptions = [
    {
        value: 'OPEN',
        label: 'OPEN',
    },
    {
        value: 'CLOSED',
        label: 'CLOSED',
    },
];

const getPositionIdsChunks = ({ allPositions, isGetOnlyOpenPositions = false, filterDatePosted = 'ALL' }) => {
    const getFirstActiveJobDateFilter = getFirstActiveJobDate();
    const datePostedFilter = datePosted();
    return _.chain(allPositions)
        .filter((item) => {
            const isValidByJobStatus = isGetOnlyOpenPositions ? filterEmployerJobsByOpenCloseState(item, 'OPEN') : true;
            if (isValidByJobStatus) {
                const postedDate = getFirstActiveJobDateFilter(item.status);
                const isValidByJobPostedDate = datePostedFilter(postedDate, filterDatePosted);
                return isValidByJobPostedDate;
            }
            return false;
        })
        .map('_id')
        .chunk(WORKFLOW_COUNTERS_CHUNK_SIZE)
        .value();
};

@withAngularComponent({
    name: 'employerJobsComponent',
    template,
    require: {
        employerCtrl: '^employerComponent',
    },
    controllerAs: 'employerJobsCtrl',
})
export class EmployerJobsComponent extends BaseComponent {
    @Inject('$state') $state;
    @Inject('$filter') $filter;
    @Inject('$stateParams') $stateParams;
    @Inject('$localStorage') $localStorage;
    @Inject('PositionService') positionService;
    @Inject('AuthService') authService;
    @Inject('EmployerService') employerService;
    @Inject('EmployerRepService') employerRepService;
    @Inject('CommonCodeService') commonCodeService;
    @Inject('EmployerWorkflowStepResolver') employerWorkflowStepResolver;
    @Inject('permissions') permissions;
    @Inject('EnvSettings') EnvSettings;
    @Inject('PositionEventsService') positionEventsService;
    @Inject('WebAppRouteService') WebAppRouteService;
    @Inject('EMPLOYER_WORKFLOW_STATUS') EMPLOYER_WORKFLOW_STATUS;

    $onInit(...args) {
        super.$onInit(...args);
        this.$scope.EMPLOYER_WORKFLOW_STATUS = this.EMPLOYER_WORKFLOW_STATUS;
        this.$scope.filterDatePostedOptions = filterDatePostedOptions;
        this.$scope.filterDatePosted = this.$localStorage.filterDatePosted || '60_DAYS';
        this.$scope.jobTitleFilter = { value: '' };
        this.$scope.FEEDBACK_COUNTERS = {
            ALL: COUNTER_ALL,
            GOOD_FIT: COUNTER_GOOD_FIT,
            NOT_FIT: COUNTER_NOT_FIT,
            NON_RATED: COUNTER_NON_RATED,
        };
        this.employerPositionsFiltered = [];

        this.$scope.RATING_TOOLTIPS = {
            GOOD_FIT: 'EMPLOYER_JOBS_LIST_NONE_THUMBSUP_TOOLTIP',
            NOT_FIT: 'EMPLOYER_JOBS_LIST_NONE_THUMBSDOWN_TOOLTIP',
            NON_RATED: 'EMPLOYER_JOBS_LIST_NONE_RATED_TOOLTIP',
        };

        this.$scope.filterPositionStatusOptions = filterPositionStatusOptions;
        this.$scope.filterDatePostedOptions = filterDatePostedOptions;
        this.$scope.filterOpenClosedStatusOptions = filterOpenClosedStatusOptions;
        this.$scope.filterEmployerRepOptions = [
            {
                value: 'ALL',
                label: this.$filter('translate')('ALL'),
            },
        ];

        this.$scope
            .$watch(scope => scope.jobTitleFilter.value, (newValue) => {
                this.onFilterStateChange();
            });
    }

    onClearJobTitleFilter() {
        this.$scope.jobTitleFilter.value = '';
    }

    componentDataWillSetup() {
        this.isTalenyaUser = this.permissions.isTalenyaUser();
        this.isTEUser = this.permissions.isTalentExpertUser();
        this.isUploadWizardAvailable = true;
    }

    @Async()
    async setupComponentData() {
        if (!this.employerCtrl.employerData.positions.length > 0) {
            this.employerPositions = [];

            return;
        }
        const employerId = this.employerCtrl.employerData._id;

        const [
            { result: employerPositions },
            { result: employerReps },
        ] = await Promise.all([
            this.positionService.getPositions({
                positionIDs: this.employerCtrl.employerData.positions,
                projection: ['feedbackOnCandidates', 'generatedFeedbackTalents', 'events', 'serviceType'],
            }),
            this.employerRepService.getEmployersRepSearch({
                employerId,
            }),
        ]);

        this.employerPositions = employerPositions;

        for (const position of this.employerPositions) {
            position.locationData = this.commonCodeService.getLocationData(position);
        }

        const employerRepFilterOptions = _.chain(employerReps)
            .map(i => ({
                value: i._id,
                label: `${i.name}<br /> <span class="employer-rep-mail">${i.email}</span>`,
                labelShort: i.name,
            }))
            .orderBy([i => i.label.toLowerCase()])
            .value();

        this.$scope.filterEmployerRepOptions = [
            ...this.$scope.filterEmployerRepOptions,
            ...employerRepFilterOptions,
        ];
        const currentUserEntityId = this.authService.getCurrentUserEntityId();
        const matchedEmployerRep = _.find(employerRepFilterOptions, i => i.value === currentUserEntityId);


        this.filterState = {
            jobStatusFilter: 'ALL',
            positionPostedDateFilter: _.get(this.$localStorage, 'positionPostedDateFilter', '60_DAYS'),
            positionOpenClosedStatusFilter: 'OPEN',
            positionEmployerRepFilter: matchedEmployerRep ? matchedEmployerRep.value : 'ALL',
        };

        const sortedPositions = this.getRequestFeedback();
        this.employerPositionsFiltered = this.filterPositions(this.employerPositions);
        await this.addWorkflowCountersInfoIntoPositions({ positions: sortedPositions });
    }

    async addWorkflowCountersInfoIntoPositions({ positions }) {
        const { positionPostedDateFilter:filterDatePosted } = this.filterState;
        const chunkedAllPositionIds = getPositionIdsChunks({ allPositions: positions });
        const chunkedInitialLoadPositionIds = getPositionIdsChunks({
            allPositions: positions,
            isGetOnlyOpenPositions: true,
            filterDatePosted,
        });

        const firstChunk = chunkedInitialLoadPositionIds.shift();

        if (!_.isEmpty(firstChunk)) {
            const { result: workflowCountersFirstChunk } = await this.employerService.getEmployerWorkflowCounters(
                this.employerCtrl.employerData._id,
                { positionIDs: firstChunk },
            );

            for (const workflowCountersItem of workflowCountersFirstChunk) {
                const position = _.find(this.employerPositions, { _id: workflowCountersItem.positionId });
                if (position) {
                    position.workflowCounters = workflowCountersItem.table;
                }
            }
        }

        for (const oneChunk of chunkedAllPositionIds) {
            this.employerService.getEmployerWorkflowCounters(
                this.employerCtrl.employerData._id,
                { positionIDs: oneChunk },
            ).then(({ result: workflowCounters }) => {
                for (const workflowCountersItem of workflowCounters) {
                    const position = _.find(this.employerPositions, { _id: workflowCountersItem.positionId });

                    if (position) {
                        position.workflowCounters = workflowCountersItem.table;
                    }
                }
            });
        }
    }

    onPositionLinkClick(event, positionIndex) {
        const position = this.employerPositionsFiltered[positionIndex];
        if (position.onLinkClickHandler === OPEN_POSITION_HANDLER) {
            return this.openPosition(positionIndex);
        }

        return this.onPositionClick(position);
    }

    async openPosition(index) {
        const positionId = this.employerPositionsFiltered[index]._id;
        const employerId = this.employerCtrl.employerData._id;
        const positionStatus = this.employerPositionsFiltered[index].status.current;
        const workflowStep = await this.employerWorkflowStepResolver.getWorkflowStep(positionId, employerId);

        if (positionStatus === 'Draft') {
            return this.$state.go('employer.createJob', { employerId, positionId });
        }

        if (positionStatus === 'Ready' || positionStatus === 'Cancelled' || positionStatus === 'Done') {
            return this.WebAppRouteService.goToViewJobPage({
                employerId,
                positionId,
            });
        }

        this.$localStorage.positionData = null;

        return this.WebAppRouteService.goToEmployerCandidatesPage({
            employerId,
            positionId,
            workflowStep,
        });
    }

    goToPositionState(positionId, state, feedbackFilterMode) {
        const employerId = this.employerCtrl.employerData._id;
        const currentPosition = _.find(this.employerPositionsFiltered, { _id: positionId });

        if (currentPosition.isCompleteJobDefinitionAvailable) {
            return this.WebAppRouteService.goPositionEditPage({
                employerId,
                positionId,
            });
        }

        return this.WebAppRouteService.goToEmployerCandidatesPage({
            employerId,
            positionId,
            workflowStep: state,
            filter: feedbackFilterMode,
        });
    }

    createJob() {
        const employerId = this.employerCtrl.employerData._id;

        return this.$state.go('employer.createJob', { employerId });
    }

    getRequestFeedback() {
        const draftPos = [];
        const readyPos = [];
        const completeRatingPos = [];
        const startRatingPos = [];
        const noFeedbackPos = [];
        const doneRatingPos = [];
        const autoFeedbackRequestedPositions = [];
        const autoFeedbackInProgressPositions = [];
        const autoFeedbackCompletedPositions = [];
        const completeJobDefinitionPositions = [];

        const requestFeedbacksPositions = _.remove(this.employerPositions, (position) => {
            if (position.isCompleteJobDefinitionAvailable) {
                return true;
            }

            return ![
                positionStatuses.DONE,
                positionStatuses.CANCELLED,
                positionStatuses.FINISHED,
            ].includes(position.status.current);
        });

        _.forEach(this.employerPositions, (position) => {
            position.onLinkClickHandler = OPEN_POSITION_HANDLER;
        });

        _.forEach(requestFeedbacksPositions, (position) => {
            const { feedbackOnCandidates, generatedFeedbackTalents } = position;
            const hasGeneratedTalents = generatedFeedbackTalents ? generatedFeedbackTalents.length > 0 : false;
            const feedbackPendingCount = (_.countBy(feedbackOnCandidates,
                feedback => feedback.status === CANDIDATE_FEEDBACK_STATUSES.PENDING).true || 0);
            const feedbackAcceptedCount = (_.countBy(feedbackOnCandidates,
                feedback => feedback.status === CANDIDATE_FEEDBACK_STATUSES.ACCEPTED).true || 0);
            const isCompleteJobDefinitionAvailable = this.positionEventsService.isCompleteJobDefinitionAvailable(position);
            const feedbackEvents = position.events.filter(event => event.flowType === POSITION_EVENT_FLOW_TYPES.feedback);
            const isFeedbackCompleted = _.get(_.last(feedbackEvents), 'eventType') === POSITION_EVENTS.feedbackRatingCompleted;
            const isFeedbackRequested = _.get(_.last(feedbackEvents), 'eventType') === POSITION_EVENTS.feedbackRequested;
            const isFeedbackInProgress = _.get(_.last(feedbackEvents), 'eventType') === POSITION_EVENTS.feedbackRatingUpdated;

            switch (true) {
                case isCompleteJobDefinitionAvailable:
                    completeJobDefinitionPositions.push(position);
                    break;
                case position.status.current === 'Ready':
                    readyPos.push(position);
                    break;
                case position.status.current === 'Draft':
                    draftPos.push(position);
                    break;
                case isFeedbackRequested || (!isFeedbackInProgress && hasGeneratedTalents):
                    position.feedbackStatus = 'START_RATING';
                    autoFeedbackRequestedPositions.push(position);
                    break;
                case isFeedbackInProgress || hasGeneratedTalents:
                    position.feedbackStatus = 'COMPLETE_RATING';
                    autoFeedbackInProgressPositions.push(position);
                    break;
                case isFeedbackCompleted:
                    position.feedbackStatus = 'ENGAGING_WITH_TALENTS_FOR_YOU';
                    autoFeedbackCompletedPositions.push(position);
                    break;
                case feedbackAcceptedCount > 0 && feedbackPendingCount > 0:
                    position.feedbackStatus = 'PROVIDE_FEEDBACK_ON_PROFILE';
                    completeRatingPos.push(position);
                    break;
                case feedbackAcceptedCount > 0 && feedbackPendingCount === 0:
                    position.feedbackStatus = 'THANKS_FOR_RATING_OUR_TE_WILL_PROVIDE_PROSPECTS';
                    doneRatingPos.push(position);
                    break;
                case feedbackPendingCount > 0 && feedbackAcceptedCount === 0:
                    position.feedbackStatus = 'PROVIDE_FEEDBACK_ON_PROFILE';
                    startRatingPos.push(position);
                    break;
                default:
                    position.feedbackStatus = 'BOT_WILL_PROVIDE_PROSPECTS';
                    noFeedbackPos.push(position);
                    break;
            }

            position.feedbackPendingCount = feedbackPendingCount;
            position.feedbackAcceptedCount = feedbackAcceptedCount;
            position.feedbackAllCount = feedbackAcceptedCount + feedbackPendingCount;
            position.isCompleteJobDefinitionAvailable = isCompleteJobDefinitionAvailable;
        });

        this.requestFeedbacks = [
            ...completeJobDefinitionPositions,
            ...autoFeedbackRequestedPositions,
            ...autoFeedbackInProgressPositions,
            ...completeRatingPos,
            ...startRatingPos,
            ...autoFeedbackCompletedPositions,
            ...doneRatingPos,
            ...noFeedbackPos,
            ...readyPos,
            ...draftPos,
        ];

        const sortedPositions = [...this.requestFeedbacks, ...this.employerPositions];
        this.employerPositions = sortedPositions;
        return sortedPositions;
    }

    onFeedbackWizardClick(event, positionId) {
        const employerId = this.employerCtrl.employerData._id;

        event.stopPropagation();

        return this.WebAppRouteService.goToEmployerCandidatesPage({
            employerId,
            positionId,
            workflowStep: this.EMPLOYER_WORKFLOW_STATUS.FEEDBACK,
        });
    }

    onCreateJobClick() {
        const employerId = this.employerCtrl.employerData._id;

        return this.WebAppRouteService.goToViewUploadWizardPage({
            employerId,
            stepName: 'create',
        });
    }

    async onCompleteJobDefinitionClick(event, positionId) {
        event.stopPropagation();

        this.completeJobDefinition(positionId);
    }

    async completeJobDefinition(positionId) {
        const employerId = this.employerCtrl.employerData._id;

        this.positionIdCurrentStepFetching = positionId;

        const { stepName } = await this.positionService.getUploadJobCurrentWizardStep(positionId);

        this.positionIdCurrentStepFetching = null;
        this.applyScope();

        return this.WebAppRouteService.goToViewUploadWizardPage({
            employerId,
            positionId,
            stepName,
        });
    }

    onPositionClick(position) {
        const employerId = this.employerCtrl.employerData._id;
        const positionId = position._id;
        const status = position.status.current;
        const feedbackStatus = position.feedbackStatus;

        if (status === 'Draft') {
            return this.$state.go('employer.createJob', { positionId });
        }

        if (position.isCompleteJobDefinitionAvailable) {
            if (this.isTalenyaUser || this.isTEUser) {
                return this.WebAppRouteService.goToViewJobPage({
                    employerId,
                    positionId,
                });
            }

            return this.completeJobDefinition(positionId);
        }

        if (status === 'Ready' || status === 'Cancelled' || status === 'Done') {
            return this.WebAppRouteService.goToViewJobPage({
                employerId,
                positionId,
            });
        }

        if (_.includes(['START_RATING', 'COMPLETE_RATING', 'PROVIDE_FEEDBACK_ON_PROFILE'], feedbackStatus)) {
            return this.WebAppRouteService.goToEmployerCandidatesPage({
                employerId,
                positionId,
                workflowStep: this.EMPLOYER_WORKFLOW_STATUS.FEEDBACK,
            });
        }

        return this.WebAppRouteService.goToEmployerCandidatesPage({
            employerId,
            positionId,
            workflowStep: this.EMPLOYER_WORKFLOW_STATUS.FEEDBACK,
        });
    }

    filterPositions(positions) {
        return (positions || []).filter(position => filterEmployerJobsByOpenCloseState()(position, this.filterState.positionOpenClosedStatusFilter)
            && datePosted()(getFirstActiveJobDate()(position.status), this.filterState.positionPostedDateFilter)
            && filterEmployerJobsByTitle()(position.jobTitle, this.$scope.jobTitleFilter.value)
            && filterEmployerJobsByStatus()(position, this.filterState.jobStatusFilter)
            && filterEmployerJobsByEmployerRepId()(position, this.filterState.positionEmployerRepFilter));
    }

    onFilterStateChange(filterName, value) {
        if (filterName) {
            this.filterState[filterName] = value;
            this.$localStorage[filterName] = value;
            this.setExpandedFilterState(filterName);
        }
        this.employerPositionsFiltered = this.filterPositions(this.employerPositions);
    }

    setExpandedFilterState(filterName) {
        this.expandedFilter = filterName === this.expandedFilter ? null : filterName;
    }

    isFilterExpanded(filterName) {
        return this.expandedFilter === filterName;
    }

    goToDiversityReport() {
        const employerData = this.employerCtrl.employerData;
        const employerId = employerData._id;
        const isDiversityEnabled = employerData.isDiversityEnabled;

        if (isDiversityEnabled) {
            this.isFilterExpanded('reportsList');
            this.WebAppRouteService.goDiversityReportPage({
                employerId,
            });
        }
    }

    resetFilters() {
        this.filterState = {
            jobStatusFilter: 'ALL',
            positionPostedDateFilter: 'ALL',
            positionOpenClosedStatusFilter: 'OPEN',
            positionEmployerRepFilter: 'ALL',
        };
        this.onClearJobTitleFilter();
        this.employerPositionsFiltered = this.filterPositions(this.employerPositions);
    }
}
